import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { faqs } from './content';

const SITE_URL = 'https://love-app-8b84d.web.app';
const APP_STORE_URL = 'https://apps.apple.com/us/app/amevo-relationship-counter/id6759841754';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.portalstudio.sonsuz_ask';

const pages = {
  '/': {
    title: 'Relationship Counter & Anniversary Tracker | Amevo',
    description: 'Track your relationship, anniversaries and special dates with Amevo. See time together, explore milestones, set reminders and share beautiful love stats.',
    type: 'website',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Amevo Relationship Counter',
    description: 'Read the Amevo Privacy Policy to learn how relationship data stays on your device and how ads, subscriptions and permissions are handled.',
    type: 'article',
  },
  '/terms-and-services': {
    title: 'Terms & Conditions | Amevo Relationship Counter',
    description: 'Read the terms and conditions for using the Amevo relationship counter, anniversary tracker and reminder app on iOS and Android.',
    type: 'article',
  },
};

function setMeta(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setCanonical(url) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', url);
}

function getStructuredData(pathname) {
  if (pathname === '/') {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'Amevo',
          url: `${SITE_URL}/`,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/logo512.png`,
            width: 512,
            height: 512,
          },
          sameAs: [APP_STORE_URL, PLAY_STORE_URL],
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: `${SITE_URL}/`,
          name: 'Amevo',
          description: pages['/'].description,
          publisher: { '@id': `${SITE_URL}/#organization` },
          inLanguage: 'en',
        },
        {
          '@type': 'FAQPage',
          '@id': `${SITE_URL}/#faq`,
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
        {
          '@type': 'SoftwareApplication',
          '@id': `${SITE_URL}/#app`,
          name: 'Amevo: Relationship Counter',
          alternateName: 'Amevo',
          url: `${SITE_URL}/`,
          image: `${SITE_URL}/logo512.png`,
          description: pages['/'].description,
          applicationCategory: 'LifestyleApplication',
          applicationSubCategory: 'Relationship tracker and anniversary counter',
          operatingSystem: 'iOS 15 or later, Android',
          softwareVersion: '1.0',
          datePublished: '2026-07-13',
          inLanguage: ['en', 'tr', 'de', 'fr', 'es', 'it', 'pt', 'ru', 'nl'],
          featureList: [
            'Live relationship counter',
            'Anniversary and milestone tracking',
            'Special-date countdowns',
            'Local reminders',
            'Shareable relationship statistics',
            'On-device data storage',
          ],
          installUrl: [APP_STORE_URL, PLAY_STORE_URL],
          sameAs: [APP_STORE_URL, PLAY_STORE_URL],
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
          },
          author: { '@id': `${SITE_URL}/#organization` },
        },
      ],
    };
  }

  const page = pages[pathname];
  if (!page) return null;

  const pageName = pathname === '/privacy-policy' ? 'Privacy Policy' : 'Terms & Conditions';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}${pathname}/#webpage`,
        url: `${SITE_URL}${pathname}`,
        name: pageName,
        description: page.description,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        inLanguage: 'en',
        dateModified: '2026-07-08',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Amevo',
            item: `${SITE_URL}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: pageName,
            item: `${SITE_URL}${pathname}`,
          },
        ],
      },
    ],
  };
}

export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPathname = pathname === '/' ? pathname : pathname.replace(/\/+$/, '');
    const page = pages[normalizedPathname];
    const isKnownPage = Boolean(page);
    const title = page?.title || 'Page Not Found | Amevo';
    const description = page?.description || 'The page you requested could not be found.';
    const canonicalUrl = `${SITE_URL}${isKnownPage ? normalizedPathname : '/'}`;

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', isKnownPage ? 'index, follow, max-image-preview:large' : 'noindex, follow');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', page?.type || 'website');
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', `${SITE_URL}/logo512.png`);
    setMeta('property', 'og:image:alt', 'Amevo relationship counter app icon');
    setMeta('name', 'twitter:card', 'summary');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', `${SITE_URL}/logo512.png`);
    setCanonical(canonicalUrl);

    const data = getStructuredData(normalizedPathname);
    let script = document.getElementById('structured-data');
    if (data) {
      if (!script) {
        script = document.createElement('script');
        script.id = 'structured-data';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    } else if (script) {
      script.remove();
    }
  }, [pathname]);

  return null;
}
